import { EllipsisDirective } from '@myre/ui-primitive-ellipsis';

import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  type OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { filter, map } from 'rxjs';

import { HOME_PAGE_MENU_ADAPTER } from '../../../menu/home-page-menu.token';
import {
  type ConsolidatedMenuItem,
  type MenuBlockType,
} from '../../../menu/menu.interfaces';
import { MenuService } from '../../../menu/menu.service';

import { ActiveRouteService } from './services/active-route.service';

@Component({
  selector: 'myre-menu-item',
  templateUrl: './menu-item.component.html',
  styleUrl: './menu-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, EllipsisDirective, RouterLink, RouterLinkActive],
})
export class MenuItemComponent implements OnDestroy {
  private readonly _menuService = inject(MenuService);
  private readonly _activeRouteService = inject(ActiveRouteService);
  private readonly _router = inject(Router);
  private readonly _homePageAdapter = inject(HOME_PAGE_MENU_ADAPTER, {
    optional: true,
  });

  public readonly type = input.required<MenuBlockType>();
  public readonly item = input.required<ConsolidatedMenuItem>();

  protected readonly _expanded = signal<boolean>(true);

  private readonly _isRouterLinkActive = signal<boolean>(false);

  private readonly _currentUrl = toSignal(
    this._router.events.pipe(
      takeUntilDestroyed(),
      filter(event => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
    ),
    { initialValue: this._router.url },
  );

  /**
   * An entry links to one route but may own several: a module rebuilt behind a
   * sister route keeps its entry selected while the user is on that route.
   */
  private readonly _activeAlternatePath = computed<string | null>(() => {
    const currentPath = this._currentUrl().split('?')[0];

    return (
      (this.item().alternateAbsolutePaths ?? []).find(
        alternatePath =>
          currentPath === alternatePath ||
          currentPath.startsWith(`${alternatePath}/`),
      ) ?? null
    );
  });

  constructor() {
    effect(() => {
      const { absolutePath } = this.item();
      const activeAlternatePath = this._activeAlternatePath();

      this._activeRouteService.setRouteActiveState(
        absolutePath,
        this._isRouterLinkActive() || !!activeAlternatePath,
        activeAlternatePath ?? absolutePath,
      );
    });
  }

  /** Whether this item is the current home page. */
  protected readonly _isHome = computed<boolean>(
    () => this._homePageAdapter?.currentHomePath() === this.item().path,
  );

  protected _hasChildren = computed<boolean>(() => {
    return this.item().children?.length > 0;
  });

  /**
   * We relay the item's route information to the service,
   * which then determines if the item is the lowest active level among all active items.
   */
  protected readonly _isTheClosestRouteActive = computed(() =>
    this._activeRouteService.isTheClosestRouteActive(
      this.item().absolutePath,
    )(),
  );

  public ngOnDestroy() {
    this._activeRouteService.removeRoute(this.item().absolutePath);
  }

  /**
   * When the value of the active item changes, we relay the information to the service so it can recalculate the lowest active item.
   */
  protected _handleActiveChange(isActive: boolean): void {
    this._isRouterLinkActive.set(isActive);
  }

  protected _handleItemClick(): void {
    this._reverseExpanded();

    this._menuService.handleItemClick();
  }

  /**
   * Open the home-page selector. Stops the event so the surrounding anchor
   * neither navigates nor toggles its expanded state.
   */
  protected _openHomeSelector(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this._homePageAdapter?.openSelector();
  }

  private _reverseExpanded(): void {
    if (!this._hasChildren()) {
      return;
    }

    this._expanded.set(!this._expanded());
  }
}
